import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { AccountInfo } from '@azure/msal-browser';
import { authConfig } from '../config/auth';
import {
  acquireAccessToken,
  getActiveAccount,
  initializeAuth,
  login as loginMsal,
  logout as logoutMsal,
  authDebugLog,
} from '../services/authService';

interface AuthUser {
  name?: string;
  username?: string;
  email?: string;
  tenantId?: string;
  roles?: string[];
}

interface AuthContextValue {
  isAuthenticated: boolean;
  loading: boolean;
  user?: AuthUser;
  roles: string[];
  authEnabled: boolean;
  error?: string;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<string | null>;
  hasRole: (role?: string | string[]) => boolean;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const extractRoles = (acct?: AccountInfo | null): string[] => {
  const claims = acct?.idTokenClaims as { roles?: string[] | string } | undefined;
  const claimValue = claims?.roles;
  if (Array.isArray(claimValue)) {
    return claimValue;
  }
  if (typeof claimValue === 'string') {
    return [claimValue];
  }
  return [];
};

const matchesRequiredRole = (userRole: string, requiredRole: string): boolean => {
  if (!userRole || !requiredRole) {
    return false;
  }

  if (userRole === requiredRole) {
    return true;
  }

  // Allow app-scoped roles such as APMT.CDH.SelfService.<APP>.Editor
  // to satisfy generic role requirements like APMT.CDH.SelfService.Editor.
  const requiredParts = requiredRole.split('.');
  if (requiredParts.length < 4) {
    return false;
  }

  const permission = requiredParts[requiredParts.length - 1];
  const supportsAppScopedMatch = permission === 'editor' || permission === 'viewer' || permission === 'developer';
  if (!supportsAppScopedMatch) {
    return false;
  }

  const requiredPrefix = requiredParts.slice(0, -1).join('.');
  return userRole.startsWith(`${requiredPrefix}.`) && userRole.endsWith(`.${permission}`);
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [account, setAccount] = useState<AccountInfo | null>(() => getActiveAccount());
  const [loading, setLoading] = useState(authConfig.enabled);
  const [error, setError] = useState<string>();
  const [roles, setRoles] = useState<string[]>(() => extractRoles(account));

  useEffect(() => {
    if (!authConfig.enabled) {
      setLoading(false);
      return;
    }

    let isMounted = true;
    const bootstrap = async () => {
      setLoading(true);
      setError(undefined);
      try {
        const activeAccount = await initializeAuth();
        if (!isMounted) {
          return;
        }
        if (activeAccount) {
          setAccount(activeAccount);
          setRoles(extractRoles(activeAccount));
          setLoading(false);
          authDebugLog('Auth bootstrap resolved active account', activeAccount.username);
        } else {
          authDebugLog('No cached account found, waiting for manual login');
          if (isMounted) {
            setLoading(false);
          }
        }
      } catch (err) {
        if (isMounted) {
          setError('Unable to complete sign-in. Please try again or contact support.');
          setLoading(false);
          authDebugLog('Authentication bootstrap failed', err);
        }
      }
    };

    bootstrap();
    return () => {
      isMounted = false;
    };
  }, []);

  const logout = async () => {
    authDebugLog('User requested logout');
    setAccount(null);
    setRoles([]);
    await logoutMsal();
  };

  const refreshToken = async () => {
    if (!authConfig.enabled) {
      return null;
    }
    const token = await acquireAccessToken();
    const activeAccount = getActiveAccount();
    if (activeAccount) {
      setAccount(activeAccount);
      setRoles(extractRoles(activeAccount));
      authDebugLog('Refreshed access token for', activeAccount.username);
    }
    return token;
  };

  const hasRole = useCallback(
    (required?: string | string[]) => {
      if (!required || !authConfig.enabled) {
        return true;
      }
      const requiredRoles = (Array.isArray(required) ? required : [required])
        .map((role) => (role || '').trim().toLowerCase());
      const userRoles = (roles || []).map((role) => (role || '').trim().toLowerCase());
      return requiredRoles.some((requiredRole) =>
        userRoles.some((userRole) => matchesRequiredRole(userRole, requiredRole))
      );
    },
    [roles]
  );

  const handleLogin = async () => {
    try {
      await loginMsal();
      const activeAccount = getActiveAccount();
      if (activeAccount) {
        setAccount(activeAccount);
        setRoles(extractRoles(activeAccount));
        authDebugLog('Manual login successful for', activeAccount.username);
      }
    } catch (err) {
      authDebugLog('Manual login failed', err);
      setError('Login failed. Please try again.');
    }
  };

  const value = useMemo<AuthContextValue>(
    () => ({
      isAuthenticated: authConfig.enabled ? Boolean(account) && !loading : true,
      loading,
      user: account
        ? {
          name: account.name ?? account.username,
          username: account.username,
          email: (account.idTokenClaims as { email?: string } | undefined)?.email ?? account.username,
          tenantId: account.tenantId,
          roles,
        }
        : undefined,
      roles,
      authEnabled: authConfig.enabled,
      error,
      login: handleLogin,
      logout,
      refreshToken,
      hasRole,
    }),
    [account, loading, error, roles, hasRole]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
